
const SIERRA_CONVERSATION_STARTED_EVENT_NAME = "sierra_conversation_started";
const SIERRA_INIT_STATE_KEY = "__sierraInitDone";
let lastTrackedSierraConversationId = null;
let hasTrackedSierraConversationStartWithoutId = false;

const externalHelpers = {
    checkIfCustomer:
        typeof window.checkIfCustomer === "function" ? window.checkIfCustomer : null,
    getCorrelationContext:
        typeof window.getCorrelationContext === "function" ? window.getCorrelationContext : null,
    recordCustomEventToNewRelic:
        typeof window.recordCustomEventToNewRelic === "function"
            ? window.recordCustomEventToNewRelic
            : null,
    heapTrack: typeof window.heapTrack === "function" ? window.heapTrack : null
};

function poll(fn, timeout = 1000, interval = 100) {
    const endTime = Date.now() + timeout;
    return new Promise((resolve, reject) => {
        const checkCondition = () => {
            const result = fn();
            if (result) {
                resolve(result);
                return;
            }
            if (Date.now() < endTime) {
                setTimeout(checkCondition, interval);
                return;
            }
            reject(new Error("timed out for poll condition"));
        };
        checkCondition();
    });
}

function checkIfCustomerSafe() {
    if (externalHelpers.checkIfCustomer) {
        return Boolean(externalHelpers.checkIfCustomer());
    }
    return false;
}

function getCorrelationContextSafe() {
    if (externalHelpers.getCorrelationContext) {
        return externalHelpers.getCorrelationContext() || {};
    }
    return {};
}

function recordCustomEventToNewRelicSafe(name, payload) {
    if (externalHelpers.recordCustomEventToNewRelic) {
        externalHelpers.recordCustomEventToNewRelic(name, payload);
    }
}

function heapTrackSafe(name, payload) {
    if (externalHelpers.heapTrack) {
        externalHelpers.heapTrack(name, payload);
    } else if (window.heap && typeof window.heap.track === "function") {
        window.heap.track(name, payload);
    }
}

function getSierraConfigVariables() {
    const languageCode =
        window.LanguageState && typeof window.LanguageState.languageCode === "string"
            ? window.LanguageState.languageCode
            : (navigator.language || "tr").split("-")[0] || "tr";

    const countryCode =
        window.ShippingCountryState && typeof window.ShippingCountryState.countryCode === "string"
            ? window.ShippingCountryState.countryCode
            : (navigator.language || "tr").split("-")[1] || "tr";

    const locale = `${languageCode}-${countryCode}`;
    const heapId = window.heap && typeof window.heap.getSessionId === "function"
        ? window.heap.getSessionId()
        : null;
    const newRelicSessionId = "static-key";

    return {
        locale,
        ...(heapId && { heapId }),
        newRelicSessionId
    };
}

function getSierraConversationStartedPayload(persistenceMode, opts) {
    const correlationContext = getCorrelationContextSafe();
    const payload = {
        ...correlationContext,
        sierraConversationId: opts && opts.conversationID ? opts.conversationID : null,
        sierraPersistenceMode: persistenceMode,
        sierraHasConversationId: Boolean(opts && opts.conversationID),
        sierraCustomerTokenEnabled: false,
        sierraIsAuthenticated: checkIfCustomerSafe()
    };

    return Object.fromEntries(
        Object.entries(payload).filter(([, value]) => value !== null && value !== undefined)
    );
}

function trackSierraConversationStarted(persistenceMode, opts) {
    const conversationId = opts && opts.conversationID ? opts.conversationID : null;

    if (conversationId) {
        if (conversationId === lastTrackedSierraConversationId) return;
        lastTrackedSierraConversationId = conversationId;
    } else if (hasTrackedSierraConversationStartWithoutId) {
        return;
    } else {
        hasTrackedSierraConversationStartWithoutId = true;
    }

    try {
        const payload = getSierraConversationStartedPayload(persistenceMode, opts);
        recordCustomEventToNewRelicSafe(SIERRA_CONVERSATION_STARTED_EVENT_NAME, payload);
        heapTrackSafe(SIERRA_CONVERSATION_STARTED_EVENT_NAME, payload);
    } catch (error) {
        console.error("Failed to track Sierra conversation start", error);
    }
}

async function buildSierraConfig() {
    const isMobile = window.innerWidth <= 440;
    const persistenceMode = isMobile ? "tab" : "cookie";
    const variables = getSierraConfigVariables();

    const config = {
        display: "corner",
        hideOnClose: false,
        persistence: persistenceMode,
        canPrintTranscript: true,
        canEndConversation: true,
        confirmEndConversation: true,
        launcher: {
            backgroundColor: "#000000",
            textColor: "#ffffff",
            borderColor: "#cccccc",
            hoverBackgroundColor: "#ffffff",
            hoverTextColor: "#000000",
            hoverBorderColor: "#cccccc",
            text: "",
            icon: "oval",
            notificationColor: "ffffff",
            showUnreadIndicator: true,
            unreadIndicatorColor: "#000000",
            unreadIndicatorText: "New message",
            unreadIndicatorIcon: "circle",
            customStyle: {
                bottom: "10px",
                right: "10px",
                zIndex: "100"
            }
        },
        variables,
        onOpen() {
            const sierraContainer = document.querySelector("[data-sierra-chat-container]");
            if (sierraContainer) {
                sierraContainer.setAttribute("translate", "no");
                sierraContainer.classList.add("notranslate");
            }

            config.variables = getSierraConfigVariables();
        },
        onConversationStart(opts) {
            trackSierraConversationStarted(persistenceMode, opts);
        }
    };

    return config;
}

function clearSierraSession() {
    try {
        if (window.sierra && typeof window.sierra.endConversation === "function") {
            window.sierra.endConversation();
        }

        const domain = window.location.hostname;
        const cname = "sierra-chat-session";
        document.cookie = `${cname}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        document.cookie = `${cname}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain}`;
    } catch (error) {
        console.error("Failed to clear Sierra session on logout", error);
    }
}

async function initSierraLiveChat() {
    if (window.IS_NATIVE_APP_CLIENT) return;
    if (window[SIERRA_INIT_STATE_KEY]) return;

    const sierraConfig = await buildSierraConfig();
    window.sierraConfig = sierraConfig;

    poll(() => Boolean(window.sierra), 5000, 1000)
        .then((isSierraDefined) => {
            if (!isSierraDefined) return;

            window.openLiveAgent = () => {
                if (window.sierra && typeof window.sierra.openChatModal === "function") {
                    window.sierra.openChatModal();
                }
            };

            const hasSierraContainer = Boolean(document.querySelector("[data-sierra-chat-container]"));
            if (hasSierraContainer) {
                window[SIERRA_INIT_STATE_KEY] = true;
                return;
            }

            window[SIERRA_INIT_STATE_KEY] = true;
        })
        .catch((error) => {
            console.error("Sierra did not initialize in time", error);
        });
}

window.clearSierraSession = clearSierraSession;
window.initSierraLiveChat = initSierraLiveChat;
window.initSierraLiveChat();